import { TOP, BOTTOM, LEFT, RIGHT } from 'layout-dnd-utils/constants';
import { hoverTypes, layoutNodeTypes } from 'layout-dnd-utils/constants/dndAreaHoverPositionConstants';
import { dropTypes, dropTargetOrientation } from 'InpageEditorUI/lib/layoutSection/constants';
import { DRAG_TYPES } from 'ContentEditorUI/utils/DragAndDropConstants';

// Only row inserts over rows need to set the parentColumnId field
// From looking at what goes on in the utils, it looks like this is actually a NodePosition
// But keeping this as the type for now to make the types here a tiny bit less insane
const getParentColumnIdForRowInsert = hoverPosition => {
  if (!hoverPosition.isColumn) {
    return hoverPosition.parentId;
  }
  return null;
};
const calculateFurthestAncestorMatchingLeftOrRightEdge = hoverPositions => {
  const firstHoverPosition = hoverPositions[0];
  let horizontalIndex = 1;
  let furthestAncestorMatchingLeftOrRightEdge = firstHoverPosition;
  while (horizontalIndex < hoverPositions.length - 1 &&
  // Don't ever reach the root, only the root's rows
  firstHoverPosition.triangleHovered === hoverPositions[horizontalIndex].leftOrRightHovered && firstHoverPosition.triangleHovered === hoverPositions[horizontalIndex + 1].leftOrRightHovered && hoverPositions[horizontalIndex].isCloseToLeftOrRightEdge && hoverPositions[horizontalIndex + 1].isCloseToLeftOrRightEdge &&
  // Not 100% sure about this and horizontal promotion (backtrack later rather than skip promotion now???)
  hoverPositions[horizontalIndex + 1].parentRowHasRoomForMoreColumns) {
    furthestAncestorMatchingLeftOrRightEdge = hoverPositions[horizontalIndex + 1];
    horizontalIndex += 2;
  }
  return furthestAncestorMatchingLeftOrRightEdge;
};
const calculateDropTargetForOuterBufferZone = (firstHoverPosition, isDraggingRow) => {
  const dropTarget = {};

  // Remap the drop target type based on the relative position of the mouse outside the layout section
  if (firstHoverPosition.relativeLocation === TOP) {
    dropTarget.type = dropTypes.INSERT_ROW_BEFORE;
    dropTarget.parentColumnId = getParentColumnIdForRowInsert(firstHoverPosition);
  } else if (firstHoverPosition.relativeLocation === BOTTOM) {
    dropTarget.type = dropTypes.INSERT_ROW_AFTER;
    dropTarget.parentColumnId = getParentColumnIdForRowInsert(firstHoverPosition);
  } else if (firstHoverPosition.relativeLocation === LEFT) {
    if (isDraggingRow) {
      console.log('No dropping rows as a new columns (yet)');
      return null;
    }
    dropTarget.type = dropTypes.INSERT_COLUMN_BEFORE;
  } else if (firstHoverPosition.relativeLocation === RIGHT) {
    if (isDraggingRow) {
      console.log('No dropping rows as a new columns (yet)');
      return null;
    }
    dropTarget.type = dropTypes.INSERT_COLUMN_AFTER;
  }
  return Object.assign({}, firstHoverPosition, dropTarget);
};
const calculateTopLevelRowDropTarget = hoverPositions => {
  const firstHoverPosition = hoverPositions[0];
  const isHoveringInOutSideBufferZone = firstHoverPosition.type === hoverTypes.OUTSIDE_LAYOUT_SECTION;
  const isHoveringInMarginAreaBetweenTwoTopLevelRows = hoverPositions.length === 2 && firstHoverPosition.type === hoverTypes.BETWEEN_ROWS;
  const isHoveringInMarginAreaAboveFirstTopMostRow = !isHoveringInMarginAreaBetweenTwoTopLevelRows && hoverPositions.length === 1 && firstHoverPosition.topOrBottomHovered === TOP;
  const isHoveringInMarginAreaBelowLastTopMostRow = !isHoveringInMarginAreaBetweenTwoTopLevelRows && hoverPositions.length === 1 && firstHoverPosition.topOrBottomHovered === BOTTOM;
  const isHoveringInEmptyRow = firstHoverPosition.isRow && firstHoverPosition.type === hoverTypes.INNER_TRIANGLE && !firstHoverPosition.isModule && firstHoverPosition.numChildColumns === 0;
  const isHoveringInEmptyColumn = firstHoverPosition.isColumn && firstHoverPosition.type === hoverTypes.INNER_TRIANGLE && !firstHoverPosition.isModule && firstHoverPosition.numChildRows === 0;
  const isHoveringOverStaticSection = !!firstHoverPosition.isStaticSection;
  if (isHoveringInOutSideBufferZone) {
    return calculateDropTargetForOuterBufferZone(firstHoverPosition, true);
  } else if (isHoveringInMarginAreaBetweenTwoTopLevelRows) {
    return {
      type: dropTypes.INSERT_ROW_AFTER,
      id: firstHoverPosition.topId,
      nextRowId: firstHoverPosition.bottomId,
      parentColumnId: firstHoverPosition.parentId
    };
  } else if (isHoveringInMarginAreaAboveFirstTopMostRow) {
    return {
      type: dropTypes.PREPEND_ROW,
      id: firstHoverPosition.id,
      parentId: firstHoverPosition.id
    };
  } else if (isHoveringInMarginAreaBelowLastTopMostRow) {
    return {
      type: dropTypes.APPEND_ROW,
      id: firstHoverPosition.id,
      parentId: firstHoverPosition.id
    };
  } else if (!isHoveringOverStaticSection && (isHoveringInEmptyRow || isHoveringInEmptyColumn)) {
    return null;
  } else {
    // Else hovering over an existing section
    const topLevelRowHoveredOver = hoverPositions[hoverPositions.length - 2];
    // If we are hovering over the top / bottom margin area of a top level

    let dropTargetType;
    if (topLevelRowHoveredOver && topLevelRowHoveredOver.topOrBottomHovered === TOP) {
      dropTargetType = dropTypes.INSERT_ROW_BEFORE;
    } else if (topLevelRowHoveredOver && topLevelRowHoveredOver.topOrBottomHovered === BOTTOM) {
      dropTargetType = dropTypes.INSERT_ROW_AFTER;
    } else {
      return null;
    }
    const dropTarget = {
      type: dropTargetType,
      parentColumnId: getParentColumnIdForRowInsert(topLevelRowHoveredOver)
    };
    return Object.assign({}, topLevelRowHoveredOver, dropTarget);
  }
};
const calculateDropTargetOnInsideOfLeafColumn = (hoverPositions, dragItem) => {
  const firstHoverPosition = hoverPositions[0];
  const {
    id,
    parentId,
    triangleHovered
  } = firstHoverPosition;
  const furthestAncestorMatchingLeftOrRightEdge = calculateFurthestAncestorMatchingLeftOrRightEdge(hoverPositions);
  if (!dragItem.isRow && triangleHovered === LEFT &&
  // Note: firstHoverPosition.unitsWide should _always_ be here, but TS is not able to infer that yet
  firstHoverPosition.unitsWide !== undefined && firstHoverPosition.unitsWide > 1) {
    return {
      type: dropTypes.INSERT_COLUMN_BEFORE,
      id,
      parentId,
      previousColumnId: furthestAncestorMatchingLeftOrRightEdge.previousColumnId
    };
  } else if (!dragItem.isRow && triangleHovered === RIGHT &&
  // Note: firstHoverPosition.unitsWide should _always_ be here, but TS is not able to infer that yet
  firstHoverPosition.unitsWide !== undefined && firstHoverPosition.unitsWide > 1) {
    return {
      type: dropTypes.INSERT_COLUMN_AFTER,
      id,
      parentId,
      nextColumnId: furthestAncestorMatchingLeftOrRightEdge.nextColumnId
    };
  } else if (triangleHovered === TOP) {
    // Splits column into two rows
    return {
      type: dropTypes.PREPEND_ROW,
      id,
      parentId
    };
  } else if (triangleHovered === BOTTOM) {
    // Splits column into two rows
    return {
      type: dropTypes.APPEND_ROW,
      id,
      parentId
    };
  }
  return null;
};
const calculateDropTargetOnColumn = hoverPositions => {
  const firstHoverPosition = hoverPositions[0];
  let furthestAncestorMatchingTopOrBottomEdge = firstHoverPosition;
  let furthestAncestorMatchingLeftOrRightEdge = firstHoverPosition;
  let verticalIndex = 1;
  if (firstHoverPosition.triangleHovered === TOP || firstHoverPosition.triangleHovered === BOTTOM) {
    while (verticalIndex < hoverPositions.length - 1 &&
    // Don't ever reach the root, only the root's rows
    firstHoverPosition.triangleHovered === hoverPositions[verticalIndex].topOrBottomHovered && hoverPositions[verticalIndex].isCloseToTopOrBottomEdge) {
      furthestAncestorMatchingTopOrBottomEdge = hoverPositions[verticalIndex];
      verticalIndex += 1;
    }
  }
  if (firstHoverPosition.triangleHovered === LEFT || firstHoverPosition.triangleHovered === RIGHT) {
    furthestAncestorMatchingLeftOrRightEdge = calculateFurthestAncestorMatchingLeftOrRightEdge(hoverPositions);
    if (furthestAncestorMatchingLeftOrRightEdge.parentRowHasRoomForMoreColumns === false) {
      return null; // Don't show insert column affordance if it would create too many columns
    }
  }
  const isWrapperColumnInRowWithSiblings = firstHoverPosition === furthestAncestorMatchingTopOrBottomEdge && firstHoverPosition.isColumn && hoverPositions[1] &&
  // Note: hoverPositions[1].numChildColumns should _always_ be defined, but TS can't yet infer that
  hoverPositions[1].numChildColumns !== undefined && hoverPositions[1].numChildColumns > 1; // parent row has more than just this column

  const isSingleModuleInSection = hoverPositions.length === 3 && firstHoverPosition.isModule;

  // Ensure that we only insert rows before or after rows, not columns (in most circumstances).
  // This will prevent us from creating extra unnecessary column wrappers (e.g. prepending a new
  // row to the top module of a column with three modules creates a column of _4_ modules instead
  // of splitting the top module into a wrapper with two rows)
  if (hoverPositions[verticalIndex] && furthestAncestorMatchingTopOrBottomEdge === firstHoverPosition && furthestAncestorMatchingTopOrBottomEdge.isColumn && !isWrapperColumnInRowWithSiblings) {
    furthestAncestorMatchingTopOrBottomEdge = hoverPositions[verticalIndex];
  }
  if (firstHoverPosition.triangleHovered === LEFT) {
    return {
      type: dropTypes.INSERT_COLUMN_BEFORE,
      id: furthestAncestorMatchingLeftOrRightEdge.id,
      parentRowId: furthestAncestorMatchingLeftOrRightEdge.parentId,
      previousColumnId: furthestAncestorMatchingLeftOrRightEdge.previousColumnId
      // isRow: furthestAncestorMatchingLeftOrRightEdge.isRow,
    };
  } else if (firstHoverPosition.triangleHovered === RIGHT) {
    return {
      type: dropTypes.INSERT_COLUMN_AFTER,
      id: furthestAncestorMatchingLeftOrRightEdge.id,
      parentRowId: furthestAncestorMatchingLeftOrRightEdge.parentId,
      nextColumnId: furthestAncestorMatchingLeftOrRightEdge.nextColumnId
      // isRow: furthestAncestorMatchingLeftOrRightEdge.isRow,
    };
  } else if (firstHoverPosition.triangleHovered === TOP) {
    if (furthestAncestorMatchingTopOrBottomEdge.isColumn && furthestAncestorMatchingTopOrBottomEdge.firstChildRowId) {
      return {
        type: dropTypes.INSERT_ROW_BEFORE,
        id: furthestAncestorMatchingTopOrBottomEdge.firstChildRowId,
        isColumn: false,
        parentColumnId: furthestAncestorMatchingTopOrBottomEdge.id
      };
    } else if (isSingleModuleInSection && !furthestAncestorMatchingTopOrBottomEdge.isCloseToTopOrBottomEdge) {
      return {
        type: dropTypes.PREPEND_ROW,
        id: firstHoverPosition.id,
        parentId: firstHoverPosition.id
      };
    }
    return {
      type: dropTypes.INSERT_ROW_BEFORE,
      id: furthestAncestorMatchingTopOrBottomEdge.id,
      isColumn: furthestAncestorMatchingTopOrBottomEdge.isColumn,
      previousRowId: furthestAncestorMatchingTopOrBottomEdge.previousRowId,
      parentColumnId: getParentColumnIdForRowInsert(furthestAncestorMatchingTopOrBottomEdge)
    };
  } else if (firstHoverPosition.triangleHovered === BOTTOM) {
    if (furthestAncestorMatchingTopOrBottomEdge.isColumn && furthestAncestorMatchingTopOrBottomEdge.lastChildRowId) {
      return {
        type: dropTypes.INSERT_ROW_AFTER,
        id: furthestAncestorMatchingTopOrBottomEdge.lastChildRowId,
        isColumn: false,
        parentColumnId: furthestAncestorMatchingTopOrBottomEdge.id
      };
    } else if (isSingleModuleInSection && !furthestAncestorMatchingTopOrBottomEdge.isCloseToTopOrBottomEdge) {
      return {
        type: dropTypes.APPEND_ROW,
        id: firstHoverPosition.id,
        parentId: firstHoverPosition.id
      };
    }
    return {
      type: dropTypes.INSERT_ROW_AFTER,
      id: furthestAncestorMatchingTopOrBottomEdge.id,
      isColumn: furthestAncestorMatchingTopOrBottomEdge.isColumn,
      nextRowId: furthestAncestorMatchingTopOrBottomEdge.nextRowId,
      parentColumnId: getParentColumnIdForRowInsert(furthestAncestorMatchingTopOrBottomEdge)
    };
  } else {
    throw new Error(`Unknown triangleHovered value in calculateDropTargetOnColumn: "${firstHoverPosition.triangleHovered}"`);
  }
};
const calculateDropTargetForRow = hoverPositions => {
  const firstHoverPosition = hoverPositions[0];
  if (firstHoverPosition.type !== hoverTypes.INNER_TRIANGLE) {
    throw new Error(`Unexpected hover type in calculateDropTargetForRow: "${firstHoverPosition.type}"`);
  }

  // Is this row logic going to ever need the `furthestAncestorMatchingTopOrBottomEdge` logic?
  const {
    id,
    topOrBottomHovered,
    previousRowId,
    nextRowId,
    triangleHovered,
    delegatedNodeToInsertAround,
    isCloseToTopOrBottomEdge,
    isCloseToTopOfChildColumn,
    isCloseToBottomOfChildColumn
  } = firstHoverPosition;
  const parentColumn = hoverPositions[1];
  // Can't insert to the left or right of a layout section, so display top/bottom hover positions.
  const isParentALayoutSection = parentColumn.id === parentColumn.layoutSectionId;
  const isCloseToTopEdge = topOrBottomHovered === TOP && isCloseToTopOrBottomEdge;
  const isCloseToBottomEdge = topOrBottomHovered === BOTTOM && isCloseToTopOrBottomEdge;

  // Check to see if we are close to a child columns bounds, if so, attatch to that instead
  if (!isCloseToTopEdge && isCloseToTopOfChildColumn) {
    return {
      type: dropTypes.INSERT_ROW_BEFORE,
      id: delegatedNodeToInsertAround.id,
      nextRowId: delegatedNodeToInsertAround.nextRowId,
      isColumn: delegatedNodeToInsertAround.isColumn,
      parentColumnId: getParentColumnIdForRowInsert(
      // @ts-expect-error this is driving me a bit insane because doing {isColumn: delegatedNodeToInsertAround.isColumn, parentId: delegatedNodeToInsertAround.parentId } works
      delegatedNodeToInsertAround)
    };
  } else if (!isCloseToBottomEdge && isCloseToBottomOfChildColumn) {
    return {
      type: dropTypes.INSERT_ROW_AFTER,
      id: delegatedNodeToInsertAround.id,
      nextRowId: delegatedNodeToInsertAround.nextRowId,
      isColumn: delegatedNodeToInsertAround.isColumn,
      parentColumnId: getParentColumnIdForRowInsert(
      // @ts-expect-error again doing {isColumn: delegatedNodeToInsertAround.isColumn, parentId: delegatedNodeToInsertAround.parentId } works
      delegatedNodeToInsertAround)
    };
  } else if (!isParentALayoutSection && triangleHovered === LEFT) {
    return {
      type: dropTypes.INSERT_COLUMN_BEFORE,
      id: parentColumn.id,
      parentRowId: parentColumn.parentId,
      previousColumnId: parentColumn.previousColumnId
    };
  } else if (!isParentALayoutSection && triangleHovered === RIGHT) {
    return {
      type: dropTypes.INSERT_COLUMN_AFTER,
      id: parentColumn.id,
      parentRowId: parentColumn.parentId,
      nextColumnId: parentColumn.nextColumnId
    };
  } else if (topOrBottomHovered === TOP) {
    return {
      type: dropTypes.INSERT_ROW_BEFORE,
      id,
      previousRowId,
      parentColumnId: getParentColumnIdForRowInsert(firstHoverPosition)
    };
  } else if (topOrBottomHovered === BOTTOM) {
    return {
      type: dropTypes.INSERT_ROW_AFTER,
      id,
      nextRowId,
      parentColumnId: getParentColumnIdForRowInsert(firstHoverPosition)
    };
  } else {
    throw new Error(`Unknown topOrBottomHovered value in calculateDropTargetForRow: "${topOrBottomHovered}"`);
  }
};
export const calculateLayoutSectionDndDropTargetFromHoverPositions = (hoverPositions, dragItem) => {
  // Don't allow dropping _inside_ the thing actually being dragged (whether a single module or row/column)
  if (dragItem.dragId) {
    const hoverPositionsMinusRoot = hoverPositions.slice(0, -1);
    for (const hoverPosition of hoverPositionsMinusRoot) {
      if (hoverPosition.id === dragItem.dragId) {
        return {
          newDropTarget: null,
          isDraggingOverSelf: true
        };
      }
    }
  }
  const firstHoverPosition = hoverPositions[0];
  const isDraggingRow = dragItem.isRow;
  const isDraggingTopMostRow = dragItem.isTopMostRow;
  if (firstHoverPosition.isEmptyLayoutSection) {
    // Show drop target for empty layout section
    return {
      newDropTarget: {
        type: dropTypes.INSERT_INTO_EMPTY_COLUMN,
        id: firstHoverPosition.layoutSectionId
      },
      isDraggingOverSelf: false
    };
  } else if (isDraggingTopMostRow) {
    return {
      newDropTarget: calculateTopLevelRowDropTarget(hoverPositions),
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.type === hoverTypes.OUTSIDE_LAYOUT_SECTION) {
    return {
      newDropTarget: calculateDropTargetForOuterBufferZone(firstHoverPosition, !!isDraggingRow),
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.isRow && firstHoverPosition.type === hoverTypes.INNER_TRIANGLE && !firstHoverPosition.isModule && firstHoverPosition.numChildColumns === 0) {
    // Insert cell inside empty row
    return {
      newDropTarget: {
        type: dropTypes.INSERT_INTO_EMPTY_ROW,
        id: firstHoverPosition.id
      },
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.isColumn && firstHoverPosition.type === hoverTypes.INNER_TRIANGLE && !firstHoverPosition.isModule && firstHoverPosition.numChildRows === 0) {
    // Insert row  inside empty wrapper cell/module group
    return {
      newDropTarget: {
        type: dropTypes.INSERT_INTO_EMPTY_COLUMN,
        id: firstHoverPosition.id
      },
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.isModule && firstHoverPosition.type === hoverTypes.INNER_TRIANGLE && !firstHoverPosition.isCloseToTriangleEdge) {
    return {
      newDropTarget: calculateDropTargetOnInsideOfLeafColumn(hoverPositions, dragItem),
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.isColumn && firstHoverPosition.type === hoverTypes.INNER_TRIANGLE) {
    return {
      newDropTarget: calculateDropTargetOnColumn(hoverPositions),
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.isRow && firstHoverPosition.type === hoverTypes.INNER_TRIANGLE) {
    return {
      newDropTarget: calculateDropTargetForRow(hoverPositions),
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.type === hoverTypes.BETWEEN_COLUMNS) {
    if (firstHoverPosition.parentRowHasRoomForMoreColumns === false) {
      console.log("Too many columns, can't insert a column in", firstHoverPosition);
      return {
        newDropTarget: null,
        isDraggingOverSelf: false
      };
    }
    return {
      newDropTarget: {
        type: dropTypes.INSERT_COLUMN_AFTER,
        id: firstHoverPosition.leftId,
        parentId: firstHoverPosition.parentId,
        nextColumnId: firstHoverPosition.rightId
      },
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.type === hoverTypes.BETWEEN_ROWS) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_ROW_AFTER,
        id: firstHoverPosition.topId,
        nextRowId: firstHoverPosition.bottomId,
        parentColumnId: firstHoverPosition.parentId
      },
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.type === hoverTypes.IN_LEFT_WHITESPACE && firstHoverPosition.parentRowHasRoomForMoreColumns) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_COLUMN_BEFORE,
        id: firstHoverPosition.id,
        parentRowId: firstHoverPosition.parentId
      },
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.type === hoverTypes.IN_RIGHT_WHITESPACE && firstHoverPosition.parentRowHasRoomForMoreColumns) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_COLUMN_AFTER,
        id: firstHoverPosition.id,
        parentRowId: firstHoverPosition.parentId
      },
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.type === hoverTypes.IN_TOP_WHITESPACE) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_ROW_BEFORE,
        id: firstHoverPosition.id,
        parentColumnId: firstHoverPosition.parentId
      },
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.type === hoverTypes.IN_BOTTOM_WHITESPACE) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_ROW_AFTER,
        id: firstHoverPosition.id,
        parentColumnId: firstHoverPosition.parentId
      },
      isDraggingOverSelf: false
    };
  }

  // No drop target to render
  return {
    newDropTarget: null,
    isDraggingOverSelf: false
  };
};
function findNearestHoveredSection(hoverPositions) {
  return hoverPositions.find(hoverPos => {
    return hoverPos.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_SECTION;
  });
}
function calculateSectionDropTarget(hoverPositions) {
  // TODO: Handle empty row/column cases
  // Else hovering over an existing section
  const sectionHoveredOver = findNearestHoveredSection(hoverPositions);
  // If we are hovering over the top / bottom margin area of a top level

  if (sectionHoveredOver) {
    const dropTargetType = sectionHoveredOver.topOrBottomHovered === TOP ? dropTypes.INSERT_SECTION_BEFORE : dropTypes.INSERT_SECTION_AFTER;
    return {
      type: dropTargetType,
      targetedSectionId: sectionHoveredOver.id,
      orientation: dropTargetOrientation.HORIZONTAL
    };
  }
  return null;
}
function calculateNearestVerticalStackDropTarget({
  hoverPositions,
  triangleHovered,
  parentOrStackId
}) {
  let nearestVerticalStackPos;
  let nearestVerticalStackChildPos;
  for (let i = 0; i < hoverPositions.length; i++) {
    const hoverPos = hoverPositions[i];
    const {
      id,
      isCloseToTopOrBottomEdge
    } = hoverPos;
    const isVerticalStack = hoverPos.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_VERTICAL_STACK;
    if ((!isVerticalStack || id === parentOrStackId) && !isCloseToTopOrBottomEdge) {
      break;
    } else if (isVerticalStack && id !== parentOrStackId) {
      nearestVerticalStackPos = hoverPos;
      nearestVerticalStackChildPos = hoverPositions[i - 1];
    }
  }
  if (nearestVerticalStackPos && nearestVerticalStackChildPos) {
    if (triangleHovered === TOP) {
      if (!nearestVerticalStackChildPos.previousSiblingId) {
        return {
          type: dropTypes.PREPEND_ELEMENT,
          containerId: nearestVerticalStackPos.id,
          firstChildId: nearestVerticalStackChildPos.id,
          orientation: dropTargetOrientation.HORIZONTAL
        };
      } else {
        return {
          type: dropTypes.INSERT_ELEMENT_BEFORE,
          containerId: nearestVerticalStackPos.id,
          targetedElementId: nearestVerticalStackChildPos.id,
          previousSiblingId: nearestVerticalStackChildPos.previousSiblingId,
          orientation: dropTargetOrientation.HORIZONTAL
        };
      }
    } else if (triangleHovered === BOTTOM) {
      if (!nearestVerticalStackChildPos.nextSiblingId) {
        return {
          type: dropTypes.APPEND_ELEMENT,
          containerId: nearestVerticalStackPos.id,
          lastChildId: nearestVerticalStackChildPos.id,
          orientation: dropTargetOrientation.HORIZONTAL
        };
      } else {
        return {
          type: dropTypes.INSERT_ELEMENT_AFTER,
          containerId: nearestVerticalStackPos.id,
          targetedElementId: nearestVerticalStackChildPos.id,
          nextSiblingId: nearestVerticalStackChildPos.nextSiblingId,
          orientation: dropTargetOrientation.HORIZONTAL
        };
      }
    }
  }
  return null;
}
function calculateNearestHorizontalStackDropTarget({
  hoverPositions,
  triangleHovered,
  parentOrStackId
}) {
  let nearestHorizontalStackPos;
  let nearestHorizontalStackChildPos;
  for (let i = 0; i < hoverPositions.length; i++) {
    const hoverPos = hoverPositions[i];
    const {
      id,
      isCloseToLeftOrRightEdge
    } = hoverPos;
    const isHorizontalStack = hoverPos.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_HORIZONTAL_STACK;
    if ((!isHorizontalStack || id === parentOrStackId) && !isCloseToLeftOrRightEdge) {
      break;
    } else if (isHorizontalStack && id !== parentOrStackId) {
      nearestHorizontalStackPos = hoverPos;
      nearestHorizontalStackChildPos = hoverPositions[i - 1];
    }
  }
  if (nearestHorizontalStackPos && nearestHorizontalStackChildPos) {
    if (triangleHovered === LEFT) {
      if (!nearestHorizontalStackChildPos.previousSiblingId) {
        return {
          type: dropTypes.PREPEND_ELEMENT,
          containerId: nearestHorizontalStackPos.id,
          firstChildId: nearestHorizontalStackChildPos.id,
          orientation: dropTargetOrientation.VERTICAL
        };
      } else {
        return {
          type: dropTypes.INSERT_ELEMENT_BEFORE,
          containerId: nearestHorizontalStackPos.id,
          targetedElementId: nearestHorizontalStackChildPos.id,
          previousSiblingId: nearestHorizontalStackChildPos.previousSiblingId,
          orientation: dropTargetOrientation.VERTICAL
        };
      }
    } else if (triangleHovered === RIGHT) {
      if (!nearestHorizontalStackChildPos.nextSiblingId) {
        return {
          type: dropTypes.APPEND_ELEMENT,
          containerId: nearestHorizontalStackPos.id,
          lastChildId: nearestHorizontalStackChildPos.id,
          orientation: dropTargetOrientation.VERTICAL
        };
      } else {
        return {
          type: dropTypes.INSERT_ELEMENT_AFTER,
          containerId: nearestHorizontalStackPos.id,
          targetedElementId: nearestHorizontalStackChildPos.id,
          nextSiblingId: nearestHorizontalStackChildPos.nextSiblingId,
          orientation: dropTargetOrientation.VERTICAL
        };
      }
    }
  }
  return null;
}
function calculateDropTargetOnGridModule(hoverPositions) {
  const firstHoverPosition = hoverPositions[0];
  const parentHoverPosition = hoverPositions[1];
  const {
    id,
    parentId,
    isCloseToTopOrBottomEdge,
    isCloseToLeftOrRightEdge,
    triangleHovered
  } = firstHoverPosition;
  if (isCloseToTopOrBottomEdge) {
    const dropTarget = calculateNearestVerticalStackDropTarget({
      hoverPositions,
      triangleHovered,
      parentOrStackId: parentId
    });
    if (dropTarget) return dropTarget;
  }
  if (isCloseToLeftOrRightEdge) {
    const dropTarget = calculateNearestHorizontalStackDropTarget({
      hoverPositions,
      triangleHovered,
      parentOrStackId: parentId
    });
    if (dropTarget) return dropTarget;
  }
  if (parentHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_HORIZONTAL_STACK) {
    const {
      leftOrRightHovered
    } = firstHoverPosition;
    if (leftOrRightHovered === LEFT) {
      if (!firstHoverPosition.previousSiblingId) {
        return {
          type: dropTypes.PREPEND_ELEMENT,
          containerId: parentId,
          firstChildId: id,
          orientation: dropTargetOrientation.VERTICAL
        };
      }
      return {
        type: dropTypes.INSERT_ELEMENT_BEFORE,
        containerId: parentId,
        targetedElementId: id,
        previousSiblingId: firstHoverPosition.previousSiblingId,
        orientation: dropTargetOrientation.VERTICAL
      };
    }
    if (!firstHoverPosition.nextSiblingId) {
      return {
        type: dropTypes.APPEND_ELEMENT,
        containerId: parentId,
        lastChildId: id,
        orientation: dropTargetOrientation.VERTICAL
      };
    }
    return {
      type: dropTypes.INSERT_ELEMENT_AFTER,
      containerId: parentId,
      targetedElementId: id,
      nextSiblingId: firstHoverPosition.nextSiblingId,
      orientation: dropTargetOrientation.VERTICAL
    };
  } else if (parentHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_VERTICAL_STACK) {
    const {
      topOrBottomHovered
    } = firstHoverPosition;
    if (topOrBottomHovered === TOP) {
      if (!firstHoverPosition.previousSiblingId) {
        return {
          type: dropTypes.PREPEND_ELEMENT,
          containerId: parentId,
          firstChildId: id,
          orientation: dropTargetOrientation.HORIZONTAL
        };
      } else {
        return {
          type: dropTypes.INSERT_ELEMENT_BEFORE,
          containerId: parentId,
          targetedElementId: id,
          previousSiblingId: firstHoverPosition.previousSiblingId,
          orientation: dropTargetOrientation.HORIZONTAL
        };
      }
    }
    if (!firstHoverPosition.nextSiblingId) {
      return {
        type: dropTypes.APPEND_ELEMENT,
        containerId: parentId,
        lastChildId: id,
        orientation: dropTargetOrientation.HORIZONTAL
      };
    }
    return {
      type: dropTypes.INSERT_ELEMENT_AFTER,
      containerId: parentId,
      targetedElementId: id,
      nextSiblingId: firstHoverPosition.nextSiblingId,
      orientation: dropTargetOrientation.HORIZONTAL
    };
  }
  return null;
  // TODO: Handle tables
}
function calculateDropTargetOnVerticalStack(hoverPositions) {
  const firstHoverPosition = hoverPositions[0];
  if (firstHoverPosition.isCloseToTopOrBottomEdge) {
    const dropTarget = calculateNearestVerticalStackDropTarget({
      hoverPositions,
      triangleHovered: firstHoverPosition.triangleHovered,
      parentOrStackId: firstHoverPosition.id
    });
    if (dropTarget) return dropTarget;
  }
  if (firstHoverPosition.isCloseToLeftOrRightEdge) {
    const dropTarget = calculateNearestHorizontalStackDropTarget({
      hoverPositions,
      triangleHovered: firstHoverPosition.triangleHovered,
      parentOrStackId: firstHoverPosition.id
    });
    if (dropTarget) return dropTarget;
  }
  if (firstHoverPosition.type === hoverTypes.BEFORE_CHILDREN) {
    return {
      type: dropTypes.PREPEND_ELEMENT,
      containerId: firstHoverPosition.id,
      firstChildId: firstHoverPosition.firstChildId,
      orientation: dropTargetOrientation.HORIZONTAL
    };
  } else if (firstHoverPosition.type === hoverTypes.AFTER_CHILDREN) {
    return {
      type: dropTypes.APPEND_ELEMENT,
      containerId: firstHoverPosition.id,
      lastChildId: firstHoverPosition.lastChildId,
      orientation: dropTargetOrientation.HORIZONTAL
    };
  } else if (firstHoverPosition.type === hoverTypes.BETWEEN_VERTICAL_STACK_CHILDREN) {
    return {
      type: dropTypes.INSERT_ELEMENT_AFTER,
      containerId: firstHoverPosition.id,
      targetedElementId: firstHoverPosition.topId,
      nextSiblingId: firstHoverPosition.bottomId,
      orientation: dropTargetOrientation.HORIZONTAL
    };
  }
  return null;
}
function calculateDropTargetOnHorizontalStack(hoverPositions) {
  const firstHoverPosition = hoverPositions[0];
  if (firstHoverPosition.isCloseToTopOrBottomEdge) {
    const dropTarget = calculateNearestVerticalStackDropTarget({
      hoverPositions,
      triangleHovered: firstHoverPosition.triangleHovered,
      parentOrStackId: firstHoverPosition.id
    });
    if (dropTarget) return dropTarget;
  }
  if (firstHoverPosition.isCloseToLeftOrRightEdge) {
    const dropTarget = calculateNearestHorizontalStackDropTarget({
      hoverPositions,
      triangleHovered: firstHoverPosition.triangleHovered,
      parentOrStackId: firstHoverPosition.id
    });
    if (dropTarget) return dropTarget;
  }
  if (firstHoverPosition.type === hoverTypes.BEFORE_CHILDREN) {
    return {
      type: dropTypes.PREPEND_ELEMENT,
      containerId: firstHoverPosition.id,
      firstChildId: firstHoverPosition.firstChildId,
      orientation: dropTargetOrientation.VERTICAL
    };
  } else if (firstHoverPosition.type === hoverTypes.AFTER_CHILDREN) {
    return {
      type: dropTypes.APPEND_ELEMENT,
      containerId: firstHoverPosition.id,
      lastChildId: firstHoverPosition.lastChildId,
      orientation: dropTargetOrientation.VERTICAL
    };
  } else if (firstHoverPosition.type === hoverTypes.BETWEEN_HORIZONTAL_STACK_CHILDREN) {
    return {
      type: dropTypes.INSERT_ELEMENT_AFTER,
      containerId: firstHoverPosition.id,
      targetedElementId: firstHoverPosition.leftId,
      nextSiblingId: firstHoverPosition.rightId,
      orientation: dropTargetOrientation.VERTICAL
    };
  }
  return null;
}
function findGridCellHoverPosition(hoverPositions) {
  for (const hoverPosition of hoverPositions) {
    if (hoverPosition.isGridCell) {
      return hoverPosition;
    }
  }
  return null;
}
function isHoveringNearLastGridElement(hoverPositions, dragItem) {
  if (hoverPositions.length === 0 || !(dragItem.dragType === DRAG_TYPES.ADD_BUILDING_BLOCK || dragItem.dragType === DRAG_TYPES.ADD_MODULE)) {
    return false;
  }
  const [firstHoverPosition] = hoverPositions;
  if (!firstHoverPosition.isGridCell) {
    return false;
  }
  const gridTableHoverPosition = hoverPositions.find(hp => hp.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_GRID);
  const isLastChildOfGridTable = gridTableHoverPosition && gridTableHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_GRID && gridTableHoverPosition.lastChildId === firstHoverPosition.id;
  if (!isLastChildOfGridTable) {
    return false;
  }
  return Boolean(firstHoverPosition.isCloseToLeftOrRightEdge && firstHoverPosition.leftOrRightHovered === RIGHT);
}
export function calculateGridLayoutDndDropTargetFromHoverPositions(hoverPositions, dragItem) {
  if (hoverPositions.length === 0) {
    return {
      newDropTarget: null,
      isDraggingOverSelf: false
    };
  }

  // Don't allow dropping _inside_ the thing actually being dragged (whether a single module or row/column)
  if (dragItem.dragId) {
    const hoverPositionsMinusRoot = hoverPositions.slice(0, -1);
    for (const hoverPosition of hoverPositionsMinusRoot) {
      if (hoverPosition.id === dragItem.dragId) {
        return {
          newDropTarget: null,
          isDraggingOverSelf: true
        };
      }
    }
  }
  const firstHoverPosition = hoverPositions[0];
  const isDraggingSection = dragItem.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_SECTION;
  const gridCellHoverPosition = findGridCellHoverPosition(hoverPositions);
  if (firstHoverPosition.type === hoverTypes.BETWEEN_GRID_TABLE_CHILDREN) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_AFTER_TABLE_CELL,
        containerId: firstHoverPosition.id,
        targetedCellId: firstHoverPosition.leftId,
        nextSiblingId: firstHoverPosition.rightId,
        orientation: dropTargetOrientation.VERTICAL
      },
      isDraggingOverSelf: false
    };
  }
  if (firstHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_GRID && (dragItem.dragType === DRAG_TYPES.ADD_BUILDING_BLOCK || dragItem.dragType === DRAG_TYPES.ADD_MODULE)) {
    return {
      newDropTarget: {
        type: dropTypes.APPEND_GRID_CELL,
        containerId: firstHoverPosition.id,
        lastChildId: firstHoverPosition.lastChildId,
        orientation: dropTargetOrientation.VERTICAL
      },
      isDraggingOverSelf: false
    };
  }
  if (isHoveringNearLastGridElement(hoverPositions, dragItem)) {
    const gridTableHoverPosition = hoverPositions.find(hp => hp.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_GRID);
    if (gridTableHoverPosition && gridTableHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_GRID) {
      return {
        newDropTarget: {
          type: dropTypes.APPEND_GRID_CELL,
          containerId: gridTableHoverPosition.id,
          lastChildId: gridTableHoverPosition.lastChildId,
          orientation: dropTargetOrientation.VERTICAL
        },
        isDraggingOverSelf: false
      };
    }
  }
  if (gridCellHoverPosition && dragItem.layoutNodeType && dragItem.isGridCell && (gridCellHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_MODULE || gridCellHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_VERTICAL_STACK || gridCellHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_HORIZONTAL_STACK)) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_INTO_TABLE_CELL,
        containerId: gridCellHoverPosition.parentId,
        targetedCellId: gridCellHoverPosition.id
      },
      isDraggingOverSelf: false
    };
  }
  if (firstHoverPosition.isEmptyGridLayout && isDraggingSection) {
    return {
      newDropTarget: null,
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.isEmptyGridLayout) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_INTO_EMPTY_GRID_LAYOUT,
        gridLayoutId: firstHoverPosition.gridLayoutId,
        orientation: dropTargetOrientation.HORIZONTAL
      },
      isDraggingOverSelf: false
    };
  } else if (isDraggingSection) {
    return {
      newDropTarget: calculateSectionDropTarget(hoverPositions),
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_HORIZONTAL_STACK && firstHoverPosition.type === hoverTypes.INNER_TRIANGLE && firstHoverPosition.numChildren === 0) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_INTO_EMPTY_HORIZONTAL_STACK,
        containerId: firstHoverPosition.id,
        orientation: dropTargetOrientation.VERTICAL
      },
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_VERTICAL_STACK && firstHoverPosition.type === hoverTypes.INNER_TRIANGLE && firstHoverPosition.numChildren === 0) {
    return {
      newDropTarget: {
        type: dropTypes.INSERT_INTO_EMPTY_VERTICAL_STACK,
        containerId: firstHoverPosition.id,
        orientation: dropTargetOrientation.HORIZONTAL
      },
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_MODULE && firstHoverPosition.type === hoverTypes.INNER_TRIANGLE) {
    return {
      newDropTarget: calculateDropTargetOnGridModule(hoverPositions),
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_VERTICAL_STACK) {
    return {
      newDropTarget: calculateDropTargetOnVerticalStack(hoverPositions),
      isDraggingOverSelf: false
    };
  } else if (firstHoverPosition.layoutNodeType === layoutNodeTypes.GRID_LAYOUT_HORIZONTAL_STACK) {
    return {
      newDropTarget: calculateDropTargetOnHorizontalStack(hoverPositions),
      isDraggingOverSelf: false
    };
  }

  // No drop target to render
  return {
    newDropTarget: null,
    isDraggingOverSelf: false
  };
}
export const calculateDropTargetFromHoverPositions = (hoverPositions, dragItem) => {
  if (hoverPositions.length === 0) {
    return {
      newDropTarget: null,
      isDraggingOverSelf: false
    };
  }
  const firstHoverPosition = hoverPositions[0];
  if (!firstHoverPosition.gridLayoutId) {
    return calculateLayoutSectionDndDropTargetFromHoverPositions(hoverPositions, dragItem);
  } else if (firstHoverPosition.gridLayoutId) {
    return calculateGridLayoutDndDropTargetFromHoverPositions(hoverPositions, dragItem);
  }
  return {
    newDropTarget: null,
    isDraggingOverSelf: false
  };
};